import type { AccountDevice, AccountDevices } from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { Badge } from '@crowdex/ui/components/badge';
import { Button } from '@crowdex/ui/components/button';
import { Input } from '@crowdex/ui/components/input';
import { Laptop, Monitor, Smartphone } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { CloudLinkSettings } from '@/components/settings-page/cloud-link-settings';
import {
  SettingRow,
  SettingsGroup,
  SettingValue,
} from '@/components/settings-page/setting-controls';
import { ProfileAvatar } from '@/components/settings-page/settings-navigation';
import { useOrganization } from '@/components/settings-page/settings-summary';

const ROLE_LABELS = { admin: 'Admin', member: 'Member' } as const;
const PLATFORM_NAMES: Readonly<Record<string, string>> = {
  darwin: 'macOS',
  win32: 'Windows',
  linux: 'Linux',
};

const DEVICE_ICONS = {
  browser: Monitor,
  desktop: Laptop,
  phone: Smartphone,
  computer: Laptop,
} satisfies Record<AccountDevice['kind'], typeof Monitor>;

function since(iso: string | null): string {
  return iso === null ? '' : new Date(iso).toLocaleDateString();
}

/** The account's sign-ins and linked computers, each signed out from its row. */
function useAccountDevices() {
  const [devices, setDevices] = useState<AccountDevices | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setDevices(await apiFetch<AccountDevices>('/api/account/devices', { cache: 'no-store' }));
      setError(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const revoke = async (id: string) => {
    try {
      await apiFetch(`/api/account/devices/${encodeURIComponent(id)}`, { method: 'DELETE' });
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  };
  return { devices, error, revoke };
}

/** Settings › Account (D40, phone 37): the profile, Crowdex Cloud and the account's sign-ins. */
export function AccountSettingsSection({
  email,
  displayName,
  onDisplayNameChange,
  onSignOut,
}: {
  email: string;
  displayName: string;
  /** Kept once the typing pauses. */
  onDisplayNameChange: (value: string) => void;
  onSignOut: () => void;
}) {
  const organization = useOrganization();
  const { devices, error, revoke } = useAccountDevices();
  const shownName = displayName || email;
  const signOutDevice = (device: { id: string | null; label: string }) => {
    if (device.id === null) return;
    if (!window.confirm(`Sign ${device.label} out of your account?`)) return;
    void revoke(device.id);
  };
  return (
    <>
      <header className="mb-7 flex items-center gap-4 max-md:flex-col max-md:text-center">
        <ProfileAvatar name={shownName} size="xl" />
        <div className="min-w-0 flex-1">
          <h1 className="type-page-title truncate text-foreground">{shownName}</h1>
          <p className="mt-1 truncate text-muted-foreground text-sm">
            {[email, organization?.name, organization && ROLE_LABELS[organization.role]]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      </header>
      <SettingsGroup title="Profile">
        <SettingRow
          description="Shown to agents in instructions and summaries"
          title="Display name"
        >
          <Input
            aria-label="Display name"
            className="h-control-sm w-48 text-sm"
            onChange={(event) => onDisplayNameChange(event.target.value)}
            value={displayName}
          />
        </SettingRow>
        <SettingRow title="Email">
          <SettingValue>{email}</SettingValue>
        </SettingRow>
      </SettingsGroup>
      <SettingsGroup title="Crowdex Cloud">
        {organization !== null && (
          <SettingRow title="Organization">
            <SettingValue tone="foreground">
              {organization.name} · {ROLE_LABELS[organization.role]}
            </SettingValue>
          </SettingRow>
        )}
        {(devices?.computers ?? []).map((computer) => (
          <SettingRow
            description={[
              computer.platform === null
                ? null
                : (PLATFORM_NAMES[computer.platform] ?? computer.platform),
              computer.linkedAt === null ? null : `linked ${since(computer.linkedAt)}`,
              'conversations can move between the cloud and it',
            ]
              .filter(Boolean)
              .join(' · ')}
            key={computer.id ?? computer.name}
            leading={<Laptop aria-hidden className="size-4 text-muted-foreground" />}
            title={computer.name}
          >
            {computer.current ? (
              <Badge size="sm">This computer</Badge>
            ) : (
              <Button
                onClick={() => signOutDevice({ id: computer.id, label: computer.name })}
                size="sm"
                variant="outline"
              >
                Unlink
              </Button>
            )}
          </SettingRow>
        ))}
        {devices !== null && devices.computers.length === 0 && (
          <SettingRow
            description="Crowdex on a computer links to your account from its own Settings"
            title="No linked computer"
          />
        )}
      </SettingsGroup>
      <CloudLinkSettings />
      <SettingsGroup title={`Signed-in devices${devices ? ` · ${devices.devices.length}` : ''}`}>
        {error && <p className="px-4 py-3 text-destructive text-sm">{error}</p>}
        {devices === null && error === null && (
          <p className="px-4 py-3.5 text-muted-foreground text-sm">Loading…</p>
        )}
        {devices?.devices.map((device) => {
          const Icon = DEVICE_ICONS[device.kind];
          return (
            <SettingRow
              description={`Signed in ${since(device.createdAt)} · last seen ${since(device.lastActiveAt)}`}
              key={device.id}
              leading={<Icon aria-hidden className="size-4 text-muted-foreground" />}
              title={device.label}
            >
              {device.current ? (
                <Badge size="sm">This device</Badge>
              ) : (
                <Button
                  aria-label={`Sign ${device.label} out`}
                  onClick={() => signOutDevice(device)}
                  size="sm"
                  variant="outline"
                >
                  Sign out
                </Button>
              )}
            </SettingRow>
          );
        })}
      </SettingsGroup>
      <Button
        className="w-full border-destructive/60 max-md:h-12"
        onClick={onSignOut}
        variant="destructive"
      >
        Sign out
      </Button>
    </>
  );
}
